import { Loader, Modal, ModalProps, modalHoc } from '@innowise-group/mui-kit';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import * as Styled from './candidate-working-info-modal.styles';
import {
  CandidateWorkingInfo,
  useCandidatesAPI,
  useDefaultCandidateById,
  usePreoffersAPI,
  useValidationSchemaCandidate,
} from '@innowise-group/core';
import { FormProvider, useForm } from 'react-hook-form';
import { CandidateWorkingInfoForm } from '@shared-components';
import { yupResolver } from '@hookform/resolvers/yup';
import { candidateWorkingInfoFormDefaultValues } from '@constants';
import { localizedNameObject } from '@innowise-group/utilities';

const CandidateWorkingInfoModal: React.FC<ModalProps> = ({ close }) => {
  const { t } = useTranslation();

  const { workingInfoValidationSchema } = useValidationSchemaCandidate();
  const candidate = useDefaultCandidateById();
  const { fetchCandidateById, isLoading } = useCandidatesAPI();
  const { updateCandidatePreofferInfo } = usePreoffersAPI();

  const methods = useForm<CandidateWorkingInfo>({
    mode: 'all',
    reValidateMode: 'onBlur',
    resolver: yupResolver(workingInfoValidationSchema),
    defaultValues: candidateWorkingInfoFormDefaultValues,
  });

  const { handleSubmit, reset, trigger } = methods;

  useEffect(() => {
    if (!candidate) return;

    reset({
      hrmEmail: candidate.hrmEmail,
      isOutsideHrm: candidate.isOutsideHrm,
      ...candidate.candidatePreofferInfo,
    });
    trigger();
  }, []);

  const submitHandler = async (data: CandidateWorkingInfo) => {
    if (!candidate?.id) return;

    updateCandidatePreofferInfo({
      id: candidate.id,
      data,
      successCallback: () => {
        fetchCandidateById(candidate.id);
        close();
      },
    });
  };

  return (
    <Styled.Modal>
      <Modal.Header onClose={close}>
        <Styled.ModalTitle>
          <span>
            {t('pages.candidates.candidateWorkingInfoModal.title')}
            &nbsp;
            {candidate?.id && localizedNameObject(candidate).localizedFullName}
          </span>
        </Styled.ModalTitle>
      </Modal.Header>

      {isLoading ? (
        <Loader />
      ) : (
        <>
          <Styled.ModalBody>
            <Styled.Description>{t('pages.candidates.candidateWorkingInfoModal.description')}</Styled.Description>

            <FormProvider {...methods}>
              <CandidateWorkingInfoForm />
            </FormProvider>
          </Styled.ModalBody>

          <Styled.Footer>
            <Styled.ModalActions>
              <Styled.Button onClick={close} variant="outlined" color="primary">
                {t('buttons.cancel')}
              </Styled.Button>
              <Styled.Button onClick={handleSubmit(submitHandler)}>{t('buttons.save')}</Styled.Button>
            </Styled.ModalActions>
          </Styled.Footer>
        </>
      )}
    </Styled.Modal>
  );
};

export default modalHoc(CandidateWorkingInfoModal, 'candidate-working-info-modal');
